import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Table, Input, Button, Select, message, Divider } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined } from '@ant-design/icons';
import { GetDataExportCenter, ExportCenterItem, ExportCenterParams } from '@/services/api/dataExportCenter/dataExportCenterModel';
import { useModel } from '@umijs/max';
import './index.less';

const { Option } = Select;

interface DataExportCenterProps {
  visible: boolean;
  onCancel: () => void;
}

const DataExportCenter: React.FC<DataExportCenterProps> = ({ visible, onCancel }) => {
  const { initialState } = useModel('@@initialState');
  const [fileList, setFileList] = useState<ExportCenterItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchType, setSearchType] = useState<string>('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total: number) => `共 ${total} 条`,
  });

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // 获取文件列表
  const getFileList = useCallback(async () => {
    try {
      setLoading(true);
      const params: ExportCenterParams = {
        projectId: initialState?.currentUser?.projectId || 0,
        searchValue: searchText,
        dataType: searchType,
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
      };

      console.log('params', params);
      const response = await GetDataExportCenter(params);
      
      if (response.success) {
        setFileList(response.data.list);
        setPagination(prev => ({
          ...prev,
          total: response.data.total,
        }));
      } else {
        message.error(response.message || '获取文件列表失败');
      }
    } catch (error) {
      console.error('获取文件列表失败:', error);
      message.error('获取文件列表失败');
    } finally {
      setLoading(false);
    }
  }, [searchText, searchType, pagination.current, pagination.pageSize, initialState?.currentUser?.projectId]);

  // 启动轮询
  const startPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      // 使用最新的筛选条件重新获取文件列表
      getFileList();
    }, 3000);
  }, [getFileList]);

  // 停止轮询
  const stopPolling = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // 搜索处理
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    getFileList();
  };

  // 下载处理
  const handleDownload = (record: ExportCenterItem) => {
    if (record.filePath) {
      window.open(record.filePath);
    } else {
      message.error('文件路径不存在');
    }
  };

  // 表格变化处理
  const handleTableChange = (pag: any) => {
    const newPagination = {
      current: pag.current,
      pageSize: pag.pageSize,
    };
    setPagination(prev => ({
      ...prev,
      ...newPagination,
    }));
    // 重新获取数据
    getFileList();
  };

  // 关闭弹窗处理
  const handleClose = () => {
    stopPolling();
    onCancel();
  };

  // 格式化文件大小
  const formatFileSize = (size: number) => {
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(2)}KB`;
    } else {
      return `${(size / (1024 * 1024)).toFixed(2)}MB`;
    }
  };

  // 获取状态文本和样式
  const getStatusText = (status: string) => {
    switch (status) {
      case '0':
        return { text: '未导出', className: 'status-text-unexport' };
      case '1':
        return { text: '导出中', className: 'status-text-exporting' };
      case '2':
        return { text: '导出完成', className: 'status-text-exported' };
      case '3':
        return { text: '导出结果已删除', className: 'status-text-deleted' };
      case '4':
        return { text: '导出失败', className: 'status-text-error' };
      default:
        return { text: '未知状态', className: '' };
    }
  };

  // 表格列定义
  const columns: ColumnsType<ExportCenterItem> = [
    {
      title: '文件名',
      dataIndex: 'fileName',
      key: 'fileName',
    },
    {
      title: '格式',
      dataIndex: 'fileType',
      key: 'fileType',
      width: 70,
    },
    {
      title: '类型',
      dataIndex: 'dataTypeChina',
      key: 'dataTypeChina',
      width: 150,
    },
    {
      title: '大小',
      dataIndex: 'size',
      key: 'size',
      width: 100,
      render: (size: number) => formatFileSize(size),
    },
    {
      title: '状态',
      dataIndex: 'fileStatus',
      key: 'fileStatus',
      width: 100,
      render: (status: string) => {
        const { text, className } = getStatusText(status);
        return <span className={className}>{text}</span>;
      },
    },
    {
      title: '导出时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      align: 'center',
      render: (_, record) => {
        if (record.fileStatus === '2') {
          return (
            <a className="download-link" onClick={() => handleDownload(record)}>
              下载
            </a>
          );
        }
        return null;
      },
    },
  ];

  // 监听弹窗显示状态
  useEffect(() => {
    if (visible) {
      getFileList();
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [visible]);

  // 监听搜索条件变化
  useEffect(() => {
    if (visible) {
      getFileList();
      // 重新启动轮询，确保使用最新的筛选条件
      startPolling();
    }
  }, [searchText, searchType, getFileList]);

  // 监听分页变化
  useEffect(() => {
    if (visible) {
      getFileList();
      // 重新启动轮询，确保使用最新的分页条件
      startPolling();
    }
  }, [pagination.current, pagination.pageSize, getFileList]);

  return (
    <Modal
      title="导出中心"
      open={visible}
      onCancel={handleClose}
      width={1200}
      footer={null}
      destroyOnClose
      className="data-export-center-modal"
    >
      <div className="export-center">
        {/* 标题分隔线 */}
        <Divider style={{ margin: '0 0 16px 0' }} />
        
        {/* 搜索栏 */}
        <div className="search-bar">
          <div className="search-left">
            {/* 提示文本 */}
            <div className="tip-text">
              注意：您导出的文件生成后会暂存在服务器上，十五天后会自动删除。
            </div>
          </div>
          <div className="search-right">
            <Select
              style={{ width: 200 }}
              placeholder="选择按文件类型"
              value={searchType}
              onChange={setSearchType}
            >
              <Option value="">全部</Option>
              <Option value="1">工单列表</Option>
              <Option value="2">保洁工单详情</Option>
              <Option value="3">质检工单详情</Option>
              <Option value="4">临时工单详情</Option>
              <Option value="5">缺陷工单详情</Option>
              <Option value="6">工程巡检工单详情</Option>
              <Option value="11">秩序巡检工单详情</Option>
              <Option value="7">巡检人员效能分析</Option>
              <Option value="13">员工信息导出</Option>
            </Select>
            <Input
              placeholder="文件名"
              style={{ width: 200 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
            />
            <Button type="primary" className="blue-btn" onClick={handleSearch}>
              搜索
            </Button>
          </div>
        </div>

        {/* 文件列表表格容器 */}
        <div className="table-container">
          <Table
            columns={columns}
            dataSource={fileList}
            pagination={pagination}
            size="middle"
            bordered
            loading={loading}
            onChange={handleTableChange}
            rowKey="fileName"
            scroll={{ y: 400 }}
          />
        </div>

        {/* 页脚分隔线和关闭按钮 */}
        <div className="modal-footer">
          <Divider style={{ margin: '16px 0' }} />
          <div className="footer-actions">
            <Button onClick={handleClose}>关闭</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DataExportCenter;
